import { TRANSLOCO_LOADER, TranslocoLoader } from '@jsverse/transloco';
import { Injectable } from '@angular/core';

export const SUPPORTED_LANGS = ['en', 'nl'] as const;
export type SupportedLang = typeof SUPPORTED_LANGS[number];
export const DEFAULT_LANG: SupportedLang = 'nl';

export function isSupportedLang(code: unknown): code is SupportedLang {
  return typeof code === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(code);
}

// Normalizes arbitrary inputs (null, undefined, locales like "nl-NL", uppercase, legacy values)
// to one of the supported language codes, defaulting to DEFAULT_LANG.
export function resolveSafeLang(code: unknown): SupportedLang {
  if (typeof code !== 'string' || !code) return DEFAULT_LANG;
  const base = code.toLowerCase().split(/[-_]/)[0];
  return isSupportedLang(base) ? base : DEFAULT_LANG;
}

@Injectable({ providedIn: 'root' })
export class HttpLoader implements TranslocoLoader {
  // eslint-disable-next-line no-empty-function
  constructor() {}

  getTranslation(langPath: string) {
    const safe = resolveSafeLang(langPath);
    return import(`../assets/i18n/${safe}.json`)
      .then(res => res.default)
      .catch(err => {
        // Chunk-load errors can happen after a deploy rotates hashes, or on transient
        // network/CDN failures. Fall back to the default language bundle so the app
        // keeps rendering instead of throwing "Unable to load translation".
        // eslint-disable-next-line no-console
        console.warn(`[transloco] Failed to load "${safe}" translations, falling back to "${DEFAULT_LANG}".`, err);
        if (safe === DEFAULT_LANG) {
          return {};
        }
        return import(`../assets/i18n/${DEFAULT_LANG}.json`)
          .then(res => res.default)
          .catch(fallbackErr => {
            // eslint-disable-next-line no-console
            console.error('[transloco] Fallback translation load also failed.', fallbackErr);
            return {};
          });
      });
  }
}
